/*
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2026-01-06 18:09:24 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2026-07-10 11:35:31 +0800
 * @FilePath     : /ai-agent-lumina/src/stores/modules/config.js
 * @Description  : 颜色配置系统
 *
 * 可使用COLOR_PREFIX[颜色属性]{-透明度} 来使用,具体透明度可在 OPACITY_LEVELS 中查看具体存在的透明度选项
 * background: var([COLOR_PREFIX]background)
 * background: var([COLOR_PREFIX]background-85)
 *
 * 所有配置变量：
 * [COLOR_PREFIX]background-color
 * [COLOR_PREFIX]theme-color
 * [COLOR_PREFIX]card-color
 * [COLOR_PREFIX]label-color
 * [COLOR_PREFIX]button-color
 *
 * 根据主题深浅色 生成相反颜色 (一般用于文字颜色、标签颜色、边框颜色、按钮色等) 具体透明度可在 OPACITY_THEME 中查看具体存在的透明度选项
 * [COLOR_PREFIX]mirror-color{-透明度}
 */
import { getAppConfig } from '@/api'
import { defineStore, acceptHMRUpdate } from 'pinia';
import { initRangers } from '@/utils/rangers_extend_h5';
import { getColorShade } from '@/utils';
import i18n from '@/lang'
import { useUserStore } from '@/stores/modules/user'

// 默认颜色配置
const defaultColors = {
  'background': 'rgb(255, 255, 255)', // 背景色
  'head': 'rgb(255, 208, 235)', // 头部颜色
  'theme': 'rgb(255, 124, 177)', // 主题颜色
  'label': 'rgb(242, 242, 242)', // 标签颜色
  'button': 'rgb(255, 30, 160)', // 按钮颜色
};

// 镜像文字色 便于文字背景色成反差
const defaultMirror = {
  light: 'rgb(0, 0, 0)',
  dark: 'rgb(255, 255, 255)'
}

// 配置颜色透明度级别
const OPACITY_LEVELS = [5, 10, 35, 45, 50, 60, 65, 70, 80, 85, 90];
// 镜像文字色透明度级别配置
const OPACITY_THEME = [5, 15, 25, 45, 65, 85, 100];
// 配置颜色变量前缀
const COLOR_PREFIX = '--izt-'; // 请勿随意修改

export const useConfigStore = defineStore('color', {
  state: () => ({
    // 是否正在加载
    loading: false,
    // 加载文本
    loadingText: undefined,

    // 配置加载完成的 Promise：供其他模块等待 fetchColors 完成后再执行
    fetchReady: Promise.resolve(),

    // 配置字段
    mirror: 'light',

    colorConfig: { ...defaultColors }, // 颜色配置

    appId: '',
    appSecret: '',
    agentName: '',
    agentNameEn: '',
    openingRemarks: '',
    openingRemarksEn: '',
    // e展通配置
    eztConfig: {},
    // 快捷模块
    quickRequests: [],
    // 其他配置
    otherConfig: {},
    // 白名单
    urlWhiteList: [],
  }),

  getters: {
    // 获取Agent输入框占位符
    AgentInputPlaceholder: (state) => {
      const data = {}
      // 是否多个
      if (state.otherConfig?.placeholder?.visible) {
        data.en = state.otherConfig?.placeholder?.en?.values || state.otherConfig?.placeholder?.values || []
        data.cn = state.otherConfig?.placeholder?.values || []
      } else {
        data.en = state.otherConfig?.placeholder?.en?.value || state.otherConfig?.placeholder?.value || ''
        data.cn = state.otherConfig?.placeholder?.value || ''
      }
      return data[i18n.global.locale.value || 'cn']
    },
    // 获取Agent名称
    AgentName: (state) => {
      return {
        en: state.agentNameEn || state.agentName,
        cn: state.agentName
      }[i18n.global.locale.value || 'cn']
    },
    // 获取Agent开场白
    OpeningRemarks: (state) => {
      return {
        en: state.openingRemarksEn || state.openingRemarks,
        cn: state.openingRemarks
      }[i18n.global.locale.value || 'cn']
    },
    /**
     * 获取当前颜色配置
     */
    getColors: (state) => state.colorConfig,

    /**
     * 获取特定颜色值
     */
    getColor: (state) => (colorName) => {
      return state.colorConfig[colorName] || defaultColors[colorName];
    },

    /**
     * 获取CSS变量字符串（用于导出或调试）
     */
    getCssVariables: (state) => {
      let css = ':root {\n';

      Object.entries(state.colorConfig).forEach(([colorName, colorValue]) => {
        css += `  --${colorName}-color: ${colorValue};\n`;
        OPACITY_LEVELS.forEach((level) => {
          const rgbaColor = useConfigStore().setColorOpacity(
            colorValue,
            level / 100
          );
          css += `  --${colorName}-color-${level}: ${rgbaColor};\n`;
        });
      });

      css += '}\n';
      return css;
    },

    /**
     * 快捷功能
     * @author: BYM<yongming.bai@outlook.com>
     */
    quickList: (state) => {
      const userStore = useUserStore()
      return state.quickRequests.filter(item => item.visible && (!item.isLogin || userStore.isLogin))
    },
  },

  actions: {
    _applyConfig(data = {}) {
      this.colorConfig = Object.keys(defaultColors).reduce((acc, key) => {
        const val = data.colorConfig[key];
        acc[key] = (val === null || val === undefined || val === '') ? defaultColors[key] : val;
        return acc;
      }, {});
      this.appId = data.appId || '';
      this.appSecret = data.appSecret || '';
      this.agentName = data.agentName || '';
      this.agentNameEn = data.agentNameEn || '';
      this.openingRemarks = data.openingRemarks || '';
      this.openingRemarksEn = data.openingRemarksEn || '';
      this.otherConfig = data.otherConfig || {}; // 地图 埋点 邀约等
      this.urlWhiteList = data.urlWhiteList || [];
      this.quickRequests = data.quickRequests || [];
      this.eztConfig = data.eztConfig || {};
      // 初始化埋点
      if (this.otherConfig?.trackingPoint?.visible && this.otherConfig?.trackingPoint?.appId) {
        initRangers()
      }
    },
    /**
     * 显示全局 loading
     */
    showLoading(text = undefined) {
      this.loadingText = text;
      this.loading = true;
    },

    /**
     * 隐藏全局 loading
     */
    hideLoading() {
      this.loading = false;
    },

    /**
     * 编辑加载文案
     * @author: BYM<yongming.bai@outlook.com>
     */
    setLoadingText(text = undefined) {
      this.loadingText = text;
    },

    /**
     * 从接口获取颜色配置
     */
    async fetchColors(id = null) {
      this.showLoading();
      // 每次调用创建一个新的 Promise，供其他模块 await 等待本次加载完成
      let resolveFn
      this.fetchReady = new Promise(resolve => { resolveFn = resolve })

      let success = false
      try {
        const { data: result } = await getAppConfig({exhibitionId: id})
        if (result) {
          this._applyConfig(result);
          success = true
        } else {
          // 未查到配置，说明未授权，跳转未授权页面
          this.abnormalJump('401')
        }
      } catch (err) {
        // 网络异常，跳转网络异常页面
        this.abnormalJump('503')
      } finally {
        this.setColors(this.colorConfig);
        this.hideLoading();
        resolveFn()
      }
      return success
    },

    async abnormalJump (type) {
      const { default: router } = await import('@/router')
      router.replace(`/error/${type}`)
    },

    /**
     * 设置颜色配置并更新CSS变量
     */
    setColors(newColors) {
      // 只更新存在的颜色键，防止添加未定义的颜色
      Object.keys(this.colorConfig).forEach((key) => {
        if (newColors[key] !== undefined) {
          this.colorConfig[key] = newColors[key];
        }
      });

      // 更新CSS变量
      this.updateCssVariables();
    },

    /**
     * 更新CSS变量到根元素
     */
    updateCssVariables() {
      if (typeof window === 'undefined') return;
      const { style } = document.documentElement;

      Object.entries(this.colorConfig).forEach(([colorName, colorValue]) => {
        // 背景色
        style.setProperty(`${COLOR_PREFIX}${colorName}-color`, colorValue);
        OPACITY_LEVELS.forEach((level) => {
          style.setProperty(`${COLOR_PREFIX}${colorName}-color-${level}`, this.setColorOpacity(colorValue, level / 100));
        });

        // 文字颜色
        const colorShade = getColorShade(colorValue)
        OPACITY_THEME.forEach((level) => {
          style.setProperty(`${COLOR_PREFIX}${colorName}-text-color-${level}`, this.setColorOpacity(defaultMirror[colorShade], colorShade !== 'dark' ? level / 100 : 1))
        });
      });

      // 三方组件颜色适配
      style.setProperty('--van-floating-bubble-background', this.colorConfig.theme);
      style.setProperty('--van-primary-color', this.colorConfig.button);

      this.vantStyleInit();
    },

    /**
     * 设置颜色透明度
     * 支持 rgb/rgba（逗号或空格分隔）、十六进制（#RRGGBB、#RGB）格式
     */
    setColorOpacity(color, opacity) {
      let r, g, b;
      const trimmed = String(color).trim();

      // 匹配 rgb(...) / rgba(...)，同时支持逗号分隔和空格分隔（CSS Level 4）
      const rgbCommaMatch = trimmed.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i);
      const rgbSpaceMatch = !rgbCommaMatch && trimmed.match(/^rgba?\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})\s*(?:\/\s*[\d.%]+\s*)?\)$/i);
      const rgbMatch = rgbCommaMatch || rgbSpaceMatch;

      if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10);
        g = parseInt(rgbMatch[2], 10);
        b = parseInt(rgbMatch[3], 10);
      } else {
        // 十六进制处理
        let hex = trimmed.replace(/^#/, '');

        // 处理 3 位缩写，例如 "abc" 扩展为 "aabbcc"
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // 格式校验：长度必须为 6，且只能包含十六进制字符
        if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
          console.warn(`无法解析颜色: ${color}`);
          return color;
        }

        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }

      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    },

    /**
     * vant 组件样式初始化
     * @author: BYM<yongming.bai@outlook.com>
     */
    vantStyleInit() {
      const root = document.documentElement;
      root.style.setProperty('--van-action-sheet-max-height', '100%')
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}